import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';

import { PURCHASE_STEP } from '~constants';
import {
  computeSelectorDisabledStates,
  pushBeginCheckoutEvent,
  pushSelectPromotionEvent,
  pushViewPromotionEvent,
  submitCartCheckout,
} from '~helpers';
import {
  useCartEnabled,
  useContractors,
  useDateSelector,
  useParticipantSelector,
  useProductCategorySelector,
  useSimulationManager,
  useUpsell,
  useValidityCategorySelector,
} from '~hooks';
import { RestrictedAccessModal } from '~molecules';
import { addProduct, selectCartParticipantCount } from '~redux/reducers/cartSlice';
import { PurchaseDrawer } from '~templates';
import type { Product, ProductCategory, Simulation, UpsellProposal } from '~types/appsync.types';
import type { CartProduct } from '~types/cart';
import type { ProductCategoryOption } from '~types/productCategory';
import { getWidgetsConfig } from '~utils';

export interface PurchaseFlowProps {
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Callback to close the drawer */
  onClose: () => void;
  /** Called when the Drawer close transition finishes */
  onExitTransitionEnd?: () => void;
  /** Pre-set category shortname from HTML attribute (skips category selector if set) */
  initialCategoryShortname?: string;
  /** Pre-fill validity category shortname from HTML attribute */
  initialValidityCategoryShortname?: string;
  /** Pre-fill date from HTML attribute (YYYY-MM-DD) */
  initialDate?: string;
  /** Pre-fill participants from HTML attribute (same format as URL: `p[1][age]=30&p[2][conscat]=CHILD`) */
  initialParticipants?: string;
  /** When true, drawer takes full screen (mobile ProductDetails mode) */
  fullScreen?: boolean;
  /** When true, selection hooks sync state to URL params */
  syncUrl?: boolean;
  /** When true, protected products are not filtered out */
  includeProtectedProducts?: boolean;
  /** Mobile header: domain name override (used while PurchaseFlow resolves category) */
  mobileHeaderTitle?: string;
  /** Mobile header: back button handler */
  onBackClick?: () => void;
  /** Externally resolved product category (skips internal resolution when provided) */
  productCategory?: ProductCategory;
  /** Externally resolved products list */
  products?: Product[];
  /** External loading state for the product category */
  isLoadingProductCategory?: boolean;
  /** Externally resolved category options (skips useProductCategories query) */
  productCategories?: ProductCategoryOption[];
  /** External productCategories loading state */
  isLoadingProductCategories?: boolean;
  /** Whether loading productCategories failed externally */
  hasProductCategoriesError?: boolean;
  /** Whether loading the selected category failed externally */
  hasProductCategoryError?: boolean;
  /** Pre-set contractor slug from HTML attribute (skips contractor selector if set) */
  initialContractorSlug?: string;
}

/**
 * PurchaseFlow — Orchestrator organism for the purchase widget.
 *
 * Resolves the product category, manages all selection hooks (validity, date,
 * participants, simulation), and renders the PurchaseDrawer template.
 *
 * When `initialContractorSlug` is provided, product categories are filtered by contractor
 * and the contractor's resort name is displayed in the drawer.
 *
 * When `initialCategoryShortname` is provided the category selector is hidden;
 * otherwise the user picks a category inside the drawer.
 */
const PurchaseFlow = ({
  isOpen,
  onClose,
  onExitTransitionEnd,
  initialCategoryShortname = '',
  initialValidityCategoryShortname,
  initialDate,
  initialParticipants,
  fullScreen = false,
  syncUrl = true,
  includeProtectedProducts = true,
  mobileHeaderTitle,
  onBackClick,
  productCategory: externalProductCategory,
  products: externalProducts,
  isLoadingProductCategory: externalLoadingProductCategory,
  productCategories: externalProductCategories,
  isLoadingProductCategories: externalLoadingProductCategories,
  hasProductCategoriesError: externalProductCategoriesError,
  hasProductCategoryError: externalProductCategoryError,
  initialContractorSlug,
}: PurchaseFlowProps) => {
  const hasExternalData = !!externalProductCategory;

  const [contractors] = useContractors(initialContractorSlug, initialCategoryShortname !== '');
  const selectedContractor =
    initialContractorSlug && contractors.length > 0 ? contractors[0] : undefined;

  // --- Category selection + resolution ---
  const {
    selectedProductCategoryShortname,
    handleProductCategoryChange: setProductCategory,
    hasPredefinedProductCategory,
    productCategory: resolvedProductCategory,
    products: resolvedProducts,
    loadingProductCategory: resolvedLoadingProductCategory,
    productCategories: resolvedProductCategories,
    loadingProductCategories: resolvedLoadingProductCategories,
    productCategoriesError: resolvedProductCategoriesError,
    productCategoryError: resolvedProductCategoryError,
  } = useProductCategorySelector({
    contractorId: selectedContractor?.id,
    initialCategoryShortname,
    skipQueries: hasExternalData || fullScreen,
    syncUrl,
  });

  // External overrides (avoid double-fetch when parent already resolved data)
  const productCategory = externalProductCategory ?? resolvedProductCategory;
  const products = externalProducts ?? resolvedProducts;
  const loadingProductCategory = hasExternalData
    ? (externalLoadingProductCategory ?? false)
    : resolvedLoadingProductCategory;
  const productCategories = externalProductCategories ?? resolvedProductCategories;
  const loadingProductCategories =
    externalLoadingProductCategories ?? resolvedLoadingProductCategories;
  const productCategoriesError = externalProductCategoriesError ?? resolvedProductCategoriesError;
  const productCategoryError = externalProductCategoryError ?? resolvedProductCategoryError;

  // Filter products that require an access code (public flow only)
  const accessibleProducts = useMemo(
    () =>
      includeProtectedProducts
        ? (products ?? [])
        : (products?.filter((p) => !p.protectedByCode) ?? []),
    [products, includeProtectedProducts],
  );

  // --- Validity / Step ---
  const {
    validityCategory,
    selectedProduct,
    productLocked,
    isOpenProductCodeModal,
    invalidProductCode,
    configurationStep: purchaseStep,
    sortedConsumerCategories,
    sortedValidityCategories,
    handleValidityCategoryChange,
    handleProductCodeSubmit,
    handleCancelCodeModal,
    setConfigurationStep,
  } = useValidityCategorySelector({
    initialValidityCategoryShortname,
    participantsLimit: undefined,
    productCategory,
    products: accessibleProducts,
    syncUrl,
  });

  // --- Date ---
  const { date, handleDateChange } = useDateSelector({
    initialDate,
    productCategory,
    selectedProduct,
    syncUrl,
  });

  // --- Participants ---
  const {
    participants,
    selectedTimeslot,
    participantsLimit,
    participantMode,
    handleParticipantsChange,
    handleTimeslotSelected,
  } = useParticipantSelector({
    initialParticipants,
    selectedProduct,
    sortedConsumerCategories,
    syncUrl,
  });

  // --- Upsell simulation override (cross-domain: overrides sim params without changing user selection) ---
  const [upsellSimulationOverride, setUpsellSimulationOverride] = useState<{
    date: string | undefined;
    productSlug: string;
    productCategoryShortname: string;
    validityCategoryShortname: string;
  } | null>(null);

  // --- Cart feature flag + add to cart action ---
  const cartEnabled = useCartEnabled();
  const dispatch = useDispatch();
  const cartParticipantCount = useSelector(selectCartParticipantCount);

  const handleInternalAddToCart = useCallback(() => {
    const contractorId = selectedProduct?.catalog?.contractor;
    if (
      !contractorId ||
      !productCategory ||
      !validityCategory ||
      !selectedProduct ||
      participants.length === 0 ||
      !date
    ) {
      return;
    }

    dispatch(
      addProduct({
        catalogSelectable: selectedProduct.catalog.selectable ?? true,
        contractorId,
        date: format(date, 'yyyy-MM-dd'),
        displayCalendar: selectedProduct.calendarDisplay ?? true,
        participants,
        productCategoryName: productCategory.name,
        productCategoryShortname: productCategory.shortname,
        productCategoryVisible: productCategory.visible ?? true,
        productSlug: selectedProduct.slug,
        timeslot: selectedTimeslot
          ? {
              dailyBooking: selectedTimeslot.dailyBooking,
              date: selectedTimeslot.date,
              duration: selectedTimeslot.duration,
            }
          : undefined,
        validityCategoryName: validityCategory.name ?? validityCategory.shortname,
        validityCategoryShortname: validityCategory.shortname,
        validityCategoryVisible: validityCategory.visible ?? true,
      }),
    );
    setConfigurationStep(PURCHASE_STEP.CART);
  }, [
    date,
    dispatch,
    participants,
    productCategory,
    selectedProduct,
    selectedTimeslot,
    setConfigurationStep,
    validityCategory,
  ]);

  // --- Simulation ---
  const {
    simulation,
    previousSimulation,
    loadingSimulation,
    isSimulationOnError,
    availableUpsellings,
    handleAddToCartClick,
  } = useSimulationManager({
    date,
    onAddToCartInternal: cartEnabled ? handleInternalAddToCart : undefined,
    participants,
    productCategory,
    productLocked,
    selectedProduct,
    selectedTimeslot,
    upsellSimulationOverride,
    validityCategory,
  });

  // --- Upsell ---
  const handleUpsellSelectUpselling = useCallback(
    (upselling: UpsellProposal) => {
      // Override simulation params — date is passed to simulation without changing user selection
      setUpsellSimulationOverride({
        date: upselling.updatedFirstSkidate ?? undefined,
        productCategoryShortname: upselling.productCategoryShortname ?? '',
        productSlug: upselling.productSlug,
        validityCategoryShortname: upselling.validityCategoryShortname ?? '',
      });

      pushSelectPromotionEvent(upselling, selectedProduct?.slug ?? '');
    },
    [selectedProduct?.slug],
  );

  const {
    hasAcceptedUpsell,
    shouldShowUpsellStep,
    handleSelectUpsell,
    handleSkipUpsell,
    handleContinueFromUpsell,
    handleBackFromUpsell,
    resetHasAcceptedUpsell,
  } = useUpsell({
    availableUpsellings,
    onNavigateTo: setConfigurationStep,
    onSelectUpselling: handleUpsellSelectUpselling,
  });

  // Push view_promotion analytics when upsell step becomes active
  useEffect(() => {
    if (purchaseStep === PURCHASE_STEP.UPSELL && availableUpsellings.length > 0) {
      pushViewPromotionEvent(availableUpsellings, selectedProduct?.slug ?? '');
    }
  }, [purchaseStep, availableUpsellings, selectedProduct?.slug]);

  // --- Upsell selection: triggers simulation override immediately on product click ---
  const [selectedUpsellProposal, setSelectedUpsellProposal] = useState<UpsellProposal | null>(null);

  const handleUpsellSelectionChange = useCallback(
    (proposal: UpsellProposal | null) => {
      setSelectedUpsellProposal(proposal);

      if (proposal) {
        // Trigger simulation override + analytics immediately on product click
        handleSelectUpsell(proposal);
      } else {
        // Deselecting upsell: clear simulation override
        setUpsellSimulationOverride(null);
        resetHasAcceptedUpsell();
      }
    },
    [handleSelectUpsell, setUpsellSimulationOverride, resetHasAcceptedUpsell],
  );

  // --- Navigation ---
  const handleClickAddOptions = useCallback(() => {
    if (purchaseStep === PURCHASE_STEP.UPSELL) {
      if (selectedUpsellProposal) {
        // Simulation already running from product click — go directly to OPTIONS
        handleContinueFromUpsell();
        setSelectedUpsellProposal(null);
      } else {
        handleSkipUpsell();
      }
    } else if (shouldShowUpsellStep) {
      setConfigurationStep(PURCHASE_STEP.UPSELL);
    } else {
      setConfigurationStep(PURCHASE_STEP.OPTIONS);
    }
  }, [
    purchaseStep,
    selectedUpsellProposal,
    handleContinueFromUpsell,
    shouldShowUpsellStep,
    setConfigurationStep,
    handleSkipUpsell,
  ]);

  const handleClickBackButton = useCallback(() => {
    if (purchaseStep === PURCHASE_STEP.CART) {
      setConfigurationStep(PURCHASE_STEP.OPTIONS);
    } else if (purchaseStep === PURCHASE_STEP.OPTIONS && frozenUpsellingsRef.current.length > 0) {
      // User passed through UPSELL to get here — go back to UPSELL with fresh state
      setUpsellSimulationOverride(null);
      setSelectedUpsellProposal(null);
      resetHasAcceptedUpsell();
      setConfigurationStep(PURCHASE_STEP.UPSELL);
    } else if (purchaseStep === PURCHASE_STEP.UPSELL) {
      // Going back to PRODUCT — clear frozen upsellings and all upsell state
      frozenUpsellingsRef.current = [];
      setUpsellSimulationOverride(null);
      setSelectedUpsellProposal(null);
      resetHasAcceptedUpsell();
      handleBackFromUpsell();
    } else {
      setConfigurationStep(PURCHASE_STEP.PRODUCT);
    }
  }, [purchaseStep, setConfigurationStep, handleBackFromUpsell, resetHasAcceptedUpsell]);

  const handleCartCheckout = useCallback(
    (products: CartProduct[], simulation: Simulation | undefined) => {
      if (!simulation || products.length === 0) return;
      pushBeginCheckoutEvent({ products, simulation });
      submitCartCheckout(products);
    },
    [],
  );

  // --- Category change resets all downstream selections ---
  const handleProductCategoryChange = useCallback(
    (shortname: string) => {
      setProductCategory(shortname);
      handleValidityCategoryChange(undefined);
      handleDateChange(undefined);
      handleParticipantsChange([]);
      handleTimeslotSelected(undefined);
    },
    [
      setProductCategory,
      handleValidityCategoryChange,
      handleDateChange,
      handleParticipantsChange,
      handleTimeslotSelected,
    ],
  );

  // --- Disabled states ---
  const { isDateDisabled, isParticipantsDisabled } = computeSelectorDisabledStates({
    date,
    isLoading: loadingProductCategory,
    participants,
    productLocked,
    selectedProduct,
    selectedTimeslot,
    validityCategory,
  });

  // Accessible validities: only those matching accessible products
  const accessibleValidities = useMemo(() => {
    if (!accessibleProducts.length) return [];
    const accessibleShortnames = new Set(
      accessibleProducts.map((p) => p.validityCategory?.shortname).filter(Boolean),
    );
    return sortedValidityCategories.filter((v) => accessibleShortnames.has(v.shortname));
  }, [sortedValidityCategories, accessibleProducts]);

  const activeSimulation = loadingSimulation ? previousSimulation : simulation;

  // Freeze upsellings when entering the upsell step — prevents blank screen when
  // the upsell simulation returns with no upsellings for the upgraded product
  const frozenUpsellingsRef = useRef<UpsellProposal[]>([]);

  // Capture upsellings on first UPSELL entry — cleared only when going back to PRODUCT
  if (purchaseStep === PURCHASE_STEP.UPSELL) {
    if (frozenUpsellingsRef.current.length === 0 && availableUpsellings.length > 0) {
      frozenUpsellingsRef.current = availableUpsellings;
    }
  }

  const displayedUpsellings =
    purchaseStep === PURCHASE_STEP.UPSELL && frozenUpsellingsRef.current.length > 0
      ? frozenUpsellingsRef.current
      : availableUpsellings;

  return (
    <>
      <PurchaseDrawer
        isOpen={isOpen}
        onClose={onClose}
        onExitTransitionEnd={onExitTransitionEnd}
        purchaseStep={purchaseStep}
        productCategory={productCategory}
        filteredValidities={accessibleValidities}
        fullScreen={fullScreen}
        mobileHeaderProps={
          fullScreen
            ? {
                onBackClick,
                title: productCategory?.name || mobileHeaderTitle,
              }
            : undefined
        }
        date={date}
        selectedTimeslot={selectedTimeslot}
        validityCategory={validityCategory}
        product={selectedProduct}
        products={accessibleProducts}
        participants={participants}
        simulation={activeSimulation}
        participantsLimit={participantsLimit}
        isSimulationOnError={isSimulationOnError}
        isDateDisabled={isDateDisabled}
        isParticipantsDisabled={isParticipantsDisabled}
        consumerCategories={sortedConsumerCategories}
        participantMode={participantMode}
        isLoadingProductCategory={loadingProductCategory}
        isLoadingPrices={loadingSimulation}
        onChangeValidityCategory={handleValidityCategoryChange}
        onChangeDate={handleDateChange}
        onChangeParticipants={handleParticipantsChange}
        onClickAddToCart={handleAddToCartClick}
        onClickAddOptions={handleClickAddOptions}
        onClickBackButton={handleClickBackButton}
        onTimeslotSelected={handleTimeslotSelected}
        productCategories={hasPredefinedProductCategory ? undefined : productCategories}
        selectedProductCategoryShortname={
          hasPredefinedProductCategory ? undefined : selectedProductCategoryShortname
        }
        onProductCategoryChange={
          hasPredefinedProductCategory ? undefined : handleProductCategoryChange
        }
        isLoadingProductCategories={loadingProductCategories}
        hasProductCategoriesError={productCategoriesError}
        hasProductCategoryError={productCategoryError}
        availableUpsellings={displayedUpsellings}
        hasAcceptedUpsell={hasAcceptedUpsell}
        onUpsellSelectionChange={handleUpsellSelectionChange}
        selectedUpsellProposal={selectedUpsellProposal}
        shopUrl={getWidgetsConfig('data-shop-url') ?? undefined}
        selectedContractor={selectedContractor}
        onCartCheckout={handleCartCheckout}
        cartParticipantCount={cartParticipantCount}
        cartEnabled={cartEnabled}
      />
      {isOpenProductCodeModal && (
        <RestrictedAccessModal
          open
          invalidCode={invalidProductCode}
          onCancel={handleCancelCodeModal}
          onSubmit={handleProductCodeSubmit}
        />
      )}
    </>
  );
};

export default PurchaseFlow;
